'use client';

import { useMemo } from 'react';
import NextLink from 'next/link';
import { useParams } from 'next/navigation';
import classNames from 'classnames';
import { resolveReferenceProps, resolveReferenceTarget } from 'helpers/reference';
import { getLocaleFromPath } from 'project.config';
import { Reference } from 'types/reference';

export type LinkVariant =
  | 'primary'
  | 'menu-header'
  | 'menu-item'
  | 'breadcrumb'
  | 'footer'
  | 'footer2'
  | 'buttonPrimaryGreen'
  | 'buttonPrimaryNegative'
  | 'buttonPrimaryBlack'
  | 'buttonSecondary'
  | 'buttonSecondaryNegative';

export interface LinkProps extends Omit<React.ComponentProps<typeof NextLink>, 'href' | 'key'> {
  link?: string | Reference;
  variant?: LinkVariant;
}

const Link = ({
  link,
  children,
  className = '',
  variant,
  title = '',
  locale: localeProp,
  ...props
}: React.PropsWithChildren<LinkProps>) => {
  const { locale } = useParams();

  const linkUrl = useMemo(() => {
    if (!link) return '';
    if (typeof link === 'string') return link;

    return resolveReferenceTarget(link);
  }, [link]);

  const isAbsoluteLink = linkUrl && !linkUrl.startsWith('/');

  const linkMissingLocale = linkUrl && !getLocaleFromPath(linkUrl);

  const linkProps = useMemo(() => {
    if (!link || typeof link === 'string') return {};

    return resolveReferenceProps(link);
  }, [link]);

  return (
    <NextLink
      {...props}
      {...linkProps}
      href={
        isAbsoluteLink ? linkUrl : linkUrl ? (linkMissingLocale ? `/${localeProp ?? locale}${linkUrl}` : linkUrl) : '#'
      }
      className={classNames(
        {
          'cursor-pointer text-[14px] text-neutral-500 hover:text-neutral-400 lg:text-[16px]': variant === 'primary',
          'text-secondary-black hover:underline hover:underline-offset-2': variant === 'menu-item',
          'text-[14px] font-medium text-primary-black': variant === 'breadcrumb',
          'font-semibold text-white md:text-[20px]/[25.2px]': variant === 'footer',
          'font-semibold text-white md:text-[16px]/[32px] md:font-medium': variant === 'footer2',
          'cursor-pointer text-[14px] font-medium text-primary-black': variant === 'menu-header',
          'inline-flex h-[56px] items-center rounded-full px-10 pb-[2px] text-[18px]/[22.68px] font-semibold hover:shadow-button':
            variant?.startsWith('button'),
          'bg-primary-green text-white hover:bg-primary-green-mid': variant === 'buttonPrimaryGreen',
          'bg-white text-neutrals-black hover:text-primary-green-mid': variant === 'buttonPrimaryNegative',
          'bg-neutrals-black text-white hover:bg-primary-green-mid': variant === 'buttonPrimaryBlack',
          'bg-white text-neutrals-black ring-1 ring-inset ring-neutrals-black hover:bg-primary-green-mid hover:text-white':
            variant === 'buttonSecondary',
          'bg-neutrals-black text-white ring-1 ring-inset ring-white hover:bg-primary-green-mid hover:ring-0':
            variant === 'buttonSecondaryNegative',
        },
        className,
      )}
      title={title}
    >
      {children}
    </NextLink>
  );
};

export default Link;
